import { useMemo, useCallback } from 'react';
import { useAdvancedSwipeSystem } from '@/pages/prayers/hooks/useAdvancedSwipeSystem';
import { GesturePreset, GestureConfig, GestureCallbacks } from '../types';
import { createGesturePreset, validateGestureConfig } from '../gestures/gesturePresets';
import { isIOS } from '@/utils/platform';

interface UseDialogGesturesProps {
  gesturePreset: GesturePreset;
  gestureConfig?: Partial<GestureConfig>;
  gestureCallbacks: GestureCallbacks;
  enabled?: boolean;
}

const useDialogGestures = ({
  gesturePreset,
  gestureConfig,
  gestureCallbacks,
  enabled = true
}: UseDialogGesturesProps) => {
  
  // Create final gesture configuration
  const finalGestureConfig = useMemo(() => {
    // Disable gestures on iOS to prevent conflicts with native iOS gestures
    const isIOSDevice = isIOS();
    const actuallyEnabled = enabled && !isIOSDevice;
    
    if (isIOSDevice) {
      console.log('🍎 iOS detected - disabling custom gesture system to prevent conflicts');
    }
    
    if (!actuallyEnabled) {
      return {
        horizontal: { enabled: false },
        vertical: { enabled: false },
        sensitivity: { delta: 30, velocity: 200 },
        preventConflicts: true,
      } as GestureConfig;
    }

    let baseConfig: GestureConfig;
    
    if (gesturePreset === 'custom' && gestureConfig) {
      baseConfig = {
        horizontal: { enabled: false, ...gestureConfig.horizontal },
        vertical: { enabled: false, ...gestureConfig.vertical },
        sensitivity: { delta: 30, velocity: 200, ...gestureConfig.sensitivity },
        preventConflicts: gestureConfig.preventConflicts ?? true,
      };
    } else if (gesturePreset !== 'custom') {
      // Map presets for compatibility
      let mappedPreset: 'close-only' | 'expand-collapse' | 'navigation' | 'full' = 'close-only';
      if (gesturePreset === 'full-control') mappedPreset = 'full';
      else if (gesturePreset === 'disabled') mappedPreset = 'close-only';
      else if (gesturePreset === 'close-only' || gesturePreset === 'expand-collapse' || gesturePreset === 'navigation') {
        mappedPreset = gesturePreset;
      }
      baseConfig = createGesturePreset(mappedPreset, gestureCallbacks);
    } else {
      // Fallback for custom without config
      baseConfig = {
        horizontal: { enabled: false },
        vertical: { enabled: false },
        sensitivity: { delta: 30, velocity: 200 },
        preventConflicts: true,
      };
    }
    
    // Merge with custom overrides
    const mergedConfig = gestureConfig ? {
      ...baseConfig,
      ...gestureConfig,
      horizontal: {
        ...baseConfig.horizontal,
        ...gestureConfig.horizontal,
      },
      vertical: {
        ...baseConfig.vertical,
        ...gestureConfig.vertical,
      },
      sensitivity: {
        ...baseConfig.sensitivity,
        ...gestureConfig.sensitivity,
      },
    } : baseConfig;
    
    return validateGestureConfig(mergedConfig);
  }, [gesturePreset, gestureConfig, gestureCallbacks, enabled]);

  // Check if we should actually enable gestures (disabled on iOS)
  const shouldEnableGestures = enabled && !isIOS();

  // Transform GestureConfig to SwipeConfig format
  const swipeConfig = useMemo(() => {
    return {
      horizontal: {
        onLeft: finalGestureConfig.horizontal?.onLeft || (() => {}),
        onRight: finalGestureConfig.horizontal?.onRight || (() => {}),
        enabled: finalGestureConfig.horizontal?.enabled || false,
      },
      vertical: {
        onUp: finalGestureConfig.vertical?.onUp || (() => {}),
        onDown: finalGestureConfig.vertical?.onDown || (() => {}),
        enabled: finalGestureConfig.vertical?.enabled || false,
      },
      sensitivity: finalGestureConfig.sensitivity || { delta: 30, velocity: 200 },
      preventConflicts: finalGestureConfig.preventConflicts || true,
    };
  }, [finalGestureConfig]);

  // Use the advanced swipe system
  const { swipeHandlers, swipeState, resetSwipeState } = useAdvancedSwipeSystem(swipeConfig);

  // Reset gesture state when dialog opens/closes
  const resetGestureState = useCallback(() => {
    resetSwipeState();
  }, [resetSwipeState]);

  return {
    swipeHandlers: shouldEnableGestures ? swipeHandlers : {},
    swipeState,
    resetGestureState,
    gestureConfig: finalGestureConfig,
  };
};

export default useDialogGestures;
export { useDialogGestures };
